import { FC } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { DefinitionTooltip } from "src/components/common";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import type { PredictiveInsightItem, RiskTier } from "src/services/CLTVDashboardAPI";
import {
  cltvRiskTierCode,
  getCltvInsightDefinition,
  getCltvSectionDefinition,
} from "./CltvDefinitions";
import {
  CltvSectionTitle,
  insightIcon,
  insightToneColor,
  normalizeCurrencyLabel,
  RISK_TIER_COLORS,
  RISK_TIER_FALLBACK_COLOR,
  RISK_TIER_ORDER,
  type InsightDisplay,
} from "./CltvDashboardShared";
import "../Styles/CltvPredictiveInsights.scss";

type CltvPredictiveInsightsProps = {
  loading: boolean;
  predictiveInsights: PredictiveInsightItem[];
  insightRows: InsightDisplay[];
  formatCount: (_value: number | null | undefined) => string;
  formatPercent: (_value: number | null | undefined) => string;
};

const CltvPredictiveInsights: FC<CltvPredictiveInsightsProps> = ({
  loading,
  predictiveInsights,
  insightRows,
  formatCount,
  formatPercent,
}) => {
  const { t } = useTranslation();

  return (
    <article className="cltv-dashboard__panel" data-pdf-block="predictive-insights">
      <CltvSectionTitle
        title={t("cltv.sections.predictiveInsights")}
        tooltip={getCltvSectionDefinition("predictiveInsights", t)}
      />
      {loading ? (
        <LoadingSpinner compact />
      ) : (
        <div className="cltv-dashboard__insights">
          {(() => {
            const revenueItem = predictiveInsights.find(i => i.id === "revenue-growth");
            const riskTierItem = predictiveInsights.find(i => i.id === "risk-tier-analytics");
            const churnItem = predictiveInsights.find(i => i.id === "churn-alert" || i.id === "churn-risk-alert");

            return (
              <>
                {revenueItem && (
                  <div className="cltv-dashboard__pi-revenue">
                    <p className="cltv-dashboard__pi-revenue-label">
                      {revenueItem.title}
                    </p>
                    <h3 className="cltv-dashboard__pi-revenue-value">
                      {normalizeCurrencyLabel(revenueItem.formattedValue ?? revenueItem.value)}
                    </h3>
                    <p className="cltv-dashboard__pi-revenue-desc">{revenueItem.description}</p>
                  </div>
                )}

                {riskTierItem && riskTierItem.tiers && riskTierItem.tiers.length > 0 && (
                  <div className="cltv-dashboard__pi-risk">
                    <h4 className="cltv-dashboard__pi-risk-title">{riskTierItem.title}</h4>
                    <div className="cltv-dashboard__pi-tiers">
                      {riskTierItem.tiers
                        .map((tier: RiskTier, tierIndex) => ({
                          tier,
                          tierIndex,
                          code: cltvRiskTierCode(tier.tier, t),
                        }))
                        // Ordered high → medium → low. Bands the API labels in a way this
                        // app can't map keep their original position rather than jumping
                        // to the end of the list.
                        .sort(
                          (a, b) =>
                            (a.code ? RISK_TIER_ORDER[a.code] : a.tierIndex) -
                            (b.code ? RISK_TIER_ORDER[b.code] : b.tierIndex)
                        )
                        .map(({ tier, code }) => (
                        <div key={tier.tier} className="cltv-dashboard__pi-tier">
                          <div className="cltv-dashboard__pi-tier-head">
                            <span className="cltv-dashboard__pi-tier-name">{tier.tier}</span>
                            <span className="cltv-dashboard__pi-tier-count">
                              {tier.countFormattedValue ?? formatCount(tier.count)} (
                              {tier.percentageFormattedValue ?? formatPercent(tier.percentage)}
                              )
                            </span>
                          </div>
                          <div className="cltv-dashboard__progress">
                            <span
                              style={{
                                width: `${tier.percentage}%`,
                                backgroundColor: code
                                  ? RISK_TIER_COLORS[code]
                                  : RISK_TIER_FALLBACK_COLOR,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {churnItem && (
                  <div className="cltv-dashboard__pi-churn-alert">
                    <div className="cltv-dashboard__pi-churn-header">
                      <span className="cltv-dashboard__pi-churn-icon">
                        <AlertTriangle size={16} />
                      </span>
                      <span className="cltv-dashboard__pi-churn-label">
                        {churnItem.title}
                      </span>
                      {churnItem.actionLabel && (
                        <span className="cltv-dashboard__pi-churn-badge">
                          {churnItem.actionLabel}
                        </span>
                      )}
                    </div>
                    <p className="cltv-dashboard__pi-churn-count">
                      {churnItem.countLabel ??
                        t("cltv.insights.customersAtHighRisk", {
                          value: formatCount(churnItem.count ?? 0),
                        })}
                    </p>
                    <p className="cltv-dashboard__pi-churn-desc">{churnItem.description}</p>
                  </div>
                )}

                {insightRows
                  .filter(r =>
                    r.key !== "revenue-growth" &&
                    r.key !== "risk-tier-analytics" &&
                    r.key !== "churn-alert" &&
                    r.key !== "churn-risk-alert"
                  )
                  .map(insight => {
                    const iTitle = insight.raw.title;
                    const iDesc = insight.raw.description;
                    const iValue = normalizeCurrencyLabel(
                      insight.raw.formattedValue ?? insight.raw.value,
                    );
                    const iBadge = insight.raw.badge;
                    const insightTooltip = getCltvInsightDefinition(insight.key, t);
                    return (
                      <div key={insight.key} className="cltv-dashboard__insight">
                        <div className="cltv-dashboard__insight-head">
                          <div className="cltv-dashboard__insight-left">
                            <span
                              className={`cltv-dashboard__insight-icon ${insight.tone === "warning"
                                ? "cltv-dashboard__insight-icon--warning"
                                : insight.tone === "positive"
                                  ? "cltv-dashboard__insight-icon--positive"
                                  : "cltv-dashboard__insight-icon--info"
                                }`}
                            >
                              {insightIcon(insight.tone)}
                            </span>
                            <div>
                              <div className="cltv-dashboard__insight-title-row">
                                <h4>{iTitle}</h4>
                                {insightTooltip ? (
                                  <DefinitionTooltip
                                    label={iTitle}
                                    info={insightTooltip}
                                    formulaTitle={t("cltv.common.formula")}
                                  />
                                ) : null}
                              </div>
                              <p>{iDesc}</p>
                            </div>
                          </div>
                          <div className="cltv-dashboard__insight-right">
                            <strong>{iValue}</strong>
                            {iBadge ? <span>{iBadge}</span> : null}
                          </div>
                        </div>
                        <div className="cltv-dashboard__progress">
                          <span
                            style={{
                              width: `${insight.progress}%`,
                              backgroundColor: insightToneColor(insight.tone),
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </>
            );
          })()}
        </div>
      )}
    </article>
  );
};

export default CltvPredictiveInsights;
